import React from 'react';

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useToggle } from 'react-use';
import { Button, Chip, InfoBox } from '@infoods/lifebase-ui';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ControlPointRoundedIcon from '@mui/icons-material/ControlPointRounded';

import classes from './MicroNutrientsCard.module.scss';
import { WidgetEmptyPlaceholder } from '../../../../shared/components/ui-components';
import { MicroNutrientOption } from '../../../interfaces/MicroNutrientOption';
import { FoodFormValues } from '../interfaces/FoodFormValues';
import { AddMicroNutrientModal } from './AddMicroNutrientModal/AddMicroNutrientModal';

type MicroNutrientsCardProps = {
  microNutrientOptions: MicroNutrientOption[];
};

const MicroNutrientsCard = ({ microNutrientOptions }: MicroNutrientsCardProps) => {
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const { control } = useFormContext<FoodFormValues>();
  // `fields[].id` is the field-array key, not the micronutrient id - ids come from the values
  const { fields, append, remove } = useFieldArray({ control, name: 'microNutrients' });
  const microNutrients = useWatch({ control, name: 'microNutrients' });

  const availableOptions = microNutrientOptions.filter(
    (option) => !microNutrients.some(({ id }) => id === option.id),
  );

  return (
    <InfoBox
      title="Additional Micro Nutrients"
      adornment={
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddRoundedIcon />}
          disabled={!availableOptions.length}
          onClick={() => toggleIsModalOpen(true)}
          data-test-id="add-micro-nutrient-btn"
        >
          Add nutrient
        </Button>
      }
    >
      {fields.length ? (
        <div className={classes.chips}>
          {fields.map((field, index) => (
            <Chip
              key={field.id}
              label={`${field.name} ${field.amount} ${field.measurement}`}
              onDelete={() => remove(index)}
              size="medium" // same as in autocomplete component
            />
          ))}
        </div>
      ) : (
        <WidgetEmptyPlaceholder
          title="No micronutrients added yet"
          icon={<ControlPointRoundedIcon />}
          text='Click "Add nutrient" to add micronutrients.'
        />
      )}
      {isModalOpen && (
        <AddMicroNutrientModal
          options={availableOptions}
          onAdd={append}
          onClose={() => toggleIsModalOpen(false)}
        />
      )}
    </InfoBox>
  );
};

export { MicroNutrientsCard };
